import {
  IActionReference,
  IAvailableWorkflowActions,
  IRoleType,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { DeepPartial } from '@publishing-engine/domain/types';
import {
  IGetActiveRevisionResponse,
  IGetSubmissionFieldsResponse,
  IGetSubmissionWorkflowResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchAdjudication,
  fetchAdjudicationReviews,
  fetchEarlyAssessmentFormDraft,
  fetchFormSchema,
  fetchOrganizationAndJournalsInformation,
  fetchReviewExtensionDeadline,
  fetchSubmissionContext,
  fetchSubmissionFields,
  fetchSubmissionWorkflow,
  IFetchOrganizationAndJournalsInfo
} from 'src/api/queries';
import {
  IAdjudicationDetailResponse,
  IAdjudicationReview,
  IExpertFirstAssessment
} from 'src/pages/MyTask/Adjudications/adjudicationType';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError } from 'src/utils/queryClient';

type AdjudicationsContextData = {
  fieldsResponse: IGetSubmissionFieldsResponse;
  adjudication: IAdjudicationDetailResponse;
  workflowResponse: IGetSubmissionWorkflowResponse;
  availableActions: IAvailableWorkflowActions;
  adjudicationFormSchema: {
    documentSchema: IFormSchema;
  };
  revisionFormSchema: {
    documentSchema: IFormSchema;
  };
  invalidateAdjudicationContextData: () => void;
  submissionContext: ISubmissionContext;
  organizationData: IFetchOrganizationAndJournalsInfo;
  submissionId: string;
  organizationId: string;
  firstAdjudicationFormDraft:
    | DeepPartial<IExpertFirstAssessment>
    | undefined;
  activeRevisionData?: IGetActiveRevisionResponse | null;
  adjudicationExtensionDeadline:
    | {
        currentState: string;
        currentDeadline: string;
        nextDeadline: string;
        deadlineExtensionInDays: number;
      }
    | undefined;
  refetchAdjudication: () => void;
  hasSubmittedAdjudicationInCurrentRound: () => boolean;
  isInviteRequestAccepted: () => boolean;
  adjudicationReviews: IAdjudicationReview[];
};

/**
 *
 * @param workflowResponse
 * @param currentState
 * @returns Available actions on Review Workflow State
 */
function getAvailableWorkflowActions(
  workflowResponse: IGetSubmissionWorkflowResponse,
  currentState: string,
  roleContext: string
): IAvailableWorkflowActions {
  const actions =
    workflowResponse.articleWorkflow.transitionGraph.review.states[
      currentState || ''
    ]?.actions || [];

  return {
    contains: function (
      actionReference: IActionReference['name'],
      withRole?: boolean
    ) {
      return withRole
        ? actions.some(
            a => a.name === actionReference && a.roleType === roleContext
          )
        : actions.some(a => a.name === actionReference);
    }
  };
}

export const AdjudicationsDataContext = createContext<
  AdjudicationsContextData | undefined
>(undefined);

export const useAdjudicationData = (): AdjudicationsContextData => {
  const context = useContext(AdjudicationsDataContext);
  if (!context) {
    throw new Error(
      'useAdjudicationData must be used within a AdjudicationDataProvider'
    );
  }
  return context;
};

export function getAdjudicationRequestContextFromRoute() {
  if (document.location.href.match('adjudications')) {
    const { adjudicationId } = useParams();

    if (!adjudicationId) {
      throw new Error('Unable to retrieve adjudicationId from params');
    }

    return {
      roleContext: 'expert',
      adjudicationId
    };
  }

  throw new Error('Unable to retrieve adjudicationId from params');
}

const AdjudicationDataProvider = ({ children }: { children: ReactNode }) => {
  const { roleContext, adjudicationId } =
    getAdjudicationRequestContextFromRoute();
  const queryClient = useQueryClient();

  const adjudicationKey = ['adjudication-data', { adjudicationId }];
  const {
    data: adjudicationData,
    isLoading: isLoadingAdjudication,
    isError: isErrorAdjudication,
    error: errorAdjudication,
    refetch: refetchAdjudication
  } = useQuery({
    queryKey: adjudicationKey,
    queryFn: () => fetchAdjudication(adjudicationId)
  });

  const submissionFieldsQueryKey = [
    'submission-fields-adjudication',
    adjudicationId
  ];
  const {
    data: submissionFields,
    isLoading: isLoadingSubmissionFields,
    isError: isErrorFields
  } = useQuery({
    queryKey: submissionFieldsQueryKey,
    queryFn: () =>
      adjudicationData?.caseData?.submission?.id
        ? fetchSubmissionFields(
            adjudicationData?.caseData?.submission?.id,
            roleContext as IRoleType
          )
        : null,
    enabled: !!adjudicationData?.caseData?.submission?.id,
    retry: 0
  });

  const {
    data: submissionContext,
    isLoading: isLoadingContext,
    isError: isErrorContext
  } = useQuery({
    queryKey: ['submission-context', adjudicationData?.submissionContextId],
    queryFn: () =>
      adjudicationData?.submissionContextId
        ? fetchSubmissionContext(adjudicationData?.submissionContextId)
        : null
  });

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization
  } = useQuery({
    queryKey: [
      'organization',
      adjudicationData?.submissionContextId,
      submissionContext?.organizationId
    ],
    queryFn: () =>
      submissionContext?.organizationId
        ? fetchOrganizationAndJournalsInformation(
            submissionContext?.organizationId
          )
        : null
  });

  const workflowQueryKey = [
    'submission-workflow',
    adjudicationData?.caseData?.submission?.id
  ];
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    isError: isErrorWorkflow
  } = useQuery({
    queryKey: workflowQueryKey,
    queryFn: () =>
      adjudicationData?.caseData?.submission?.id
        ? fetchSubmissionWorkflow(
            adjudicationData?.caseData?.submission?.id,
            roleContext as IRoleType
          )
        : null,
    enabled: !!adjudicationData?.caseData?.submission?.id,
    retry: 0
  });

  const firstAdjudicationSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.externalAdjudication;

  const revisionAdjudicationSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.reviewerRevisionAssessment;

  const {
    data: adjudicationFormSchema,
    isLoading: isLoadingAdjudicationSchema,
    isError: isErrorAdjudicationSchema
  } = useQuery({
    queryKey: firstAdjudicationSchemaId
      ? [
          'adjudication-context',
          adjudicationData?.submissionContextId,
          'schema',
          firstAdjudicationSchemaId
        ]
      : [],
    queryFn: () =>
      adjudicationData?.submissionContextId && firstAdjudicationSchemaId
        ? fetchFormSchema(
            adjudicationData?.submissionContextId,
            firstAdjudicationSchemaId
          )
        : undefined
  });

  const {
    data: adjudicationExtensionDeadline,
    isLoading: isLoadingAdjudicationExtensionDeadline,
    isError: isErrorAdjudicationExtensionDeadline
  } = useQuery({
    queryKey: ['adjudication-extension-deadline', adjudicationData?.id],
    queryFn: () =>
      adjudicationData?.id
        ? fetchReviewExtensionDeadline(adjudicationData?.id, 'expert')
        : null
  });

  const {
    data: revisionFormSchema,
    isLoading: isLoadingRevisionSchema,
    isError: isErrorRevisionSchema
  } = useQuery({
    queryKey: revisionAdjudicationSchemaId
      ? [
          'review-context',
          adjudicationData?.submissionContextId,
          'schema',
          revisionAdjudicationSchemaId
        ]
      : [],
    queryFn: () =>
      adjudicationData?.submissionContextId && revisionAdjudicationSchemaId
        ? fetchFormSchema(
            adjudicationData?.submissionContextId,
            revisionAdjudicationSchemaId
          )
        : undefined
  });

  const {
    data: firstAdjudicationFormDraft,
    isLoading: isLoadingFirstAdjudicationFormDraft
  } = useQuery({
    queryKey: [
      'form-draft',
      adjudicationId,
      'adjudication',
      'externalAdjudication'
    ],
    queryFn: () =>
      adjudicationId
        ? fetchEarlyAssessmentFormDraft(
            adjudicationId,
            'adjudication',
            'externalAdjudication'
          )
        : undefined
  });

  const {
    data: adjudicationReviews,
    isLoading: isAdjudicationReviewLoading,
    isError: isAdjudicationReviewError
  } = useQuery({
    queryKey: ['adjudication-reviews-data', { adjudicationId }],
    queryFn: () =>
      adjudicationId ? fetchAdjudicationReviews(adjudicationId) : null
  });

  if (
    isLoadingAdjudication ||
    isLoadingSubmissionFields ||
    isLoadingWorkflow ||
    isLoadingContext ||
    isLoadingOrganization ||
    isLoadingAdjudicationSchema ||
    isLoadingRevisionSchema ||
    isLoadingFirstAdjudicationFormDraft ||
    isAdjudicationReviewLoading ||
    isLoadingAdjudicationExtensionDeadline
  ) {
    return <Loading />;
  }

  const isError =
    isErrorFields ||
    isErrorContext ||
    isErrorOrganization ||
    isErrorWorkflow ||
    isErrorAdjudication ||
    isErrorAdjudicationSchema ||
    isErrorRevisionSchema ||
    isAdjudicationReviewError ||
    isErrorAdjudicationExtensionDeadline;

  const isDataEmpty =
    !adjudicationData ||
    !submissionFields ||
    !submissionContext ||
    !organizationData ||
    !adjudicationFormSchema ||
    !workflowData ||
    !revisionFormSchema ||
    !adjudicationReviews ||
    !adjudicationExtensionDeadline;

  if (isError || isDataEmpty) {
    return isForbiddenError(errorAdjudication) ? <Forbidden /> : <NotFound />;
  }

  const {
    caseData: {
      submission: { id: submissionId }
    }
  } = adjudicationData;

  const invalidateAdjudicationContext = () => {
    queryClient.invalidateQueries({ queryKey: adjudicationKey });
    queryClient.invalidateQueries({ queryKey: submissionFieldsQueryKey });
  };

  const hasSubmittedAssessmentInCurrentRound = () => {
    // return !!adjudicationData.caseData.firstAssessment;
    return false;
  };

  const isAdjudicationRequestAccepted = () => {
    return adjudicationData.caseData.acceptance === 'accept';
  };

  return (
    <AdjudicationsDataContext.Provider
      value={{
        adjudication: adjudicationData,
        fieldsResponse: submissionFields,
        submissionId,
        workflowResponse: workflowData,
        availableActions: getAvailableWorkflowActions(
          workflowData,
          adjudicationData.workflow.currentState,
          roleContext
        ),
        organizationData: organizationData,
        submissionContext,
        organizationId: submissionContext.organizationId,
        adjudicationFormSchema,
        revisionFormSchema,
        firstAdjudicationFormDraft:
          firstAdjudicationFormDraft?.data as DeepPartial<IExpertFirstAssessment>,
        invalidateAdjudicationContextData: invalidateAdjudicationContext,
        adjudicationExtensionDeadline,
        refetchAdjudication: refetchAdjudication,
        hasSubmittedAdjudicationInCurrentRound:
          hasSubmittedAssessmentInCurrentRound,
        isInviteRequestAccepted: isAdjudicationRequestAccepted,
        adjudicationReviews
      }}
    >
      {children}
    </AdjudicationsDataContext.Provider>
  );
};

export default AdjudicationDataProvider;
